import { useCallback, useMemo } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { useNetwork, useNotification } from 'hooks';
import { kycSettingsState, loginState } from 'global-stores';
import { API_URL } from 'constant';
import {
	ActiveEmailClientFormDataState,
	ActiveEmailClientMethodState,
	ResetCredentialsState,
	EditModeState,
	EditingClientState,
	ActiveEmailClientState,
	IsEmailClientsListLoadingState,
	EmailClientsState,
	EmailClientActionLoadingState,
} from './states';
import { SUPPORTED_CLIENT, avoidTrimKeys } from '../constants';
import { IClientEmail } from './types';

export const useResetCredentials = () => {
	// global state
	const resetAllCredentials = useSetRecoilState(ResetCredentialsState);

	const resetCredential = useCallback(() => {
		const credentialKeys: string[] = [];
		SUPPORTED_CLIENT.map(({ methods }) => {
			return methods.map(method => {
				credentialKeys.push(method.key);
			});
		});
		credentialKeys.map(key => {
			resetAllCredentials(key);
		});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return { resetCredential };
};

// New hook for edit functionality
export const useEditEmailClient = () => {
	const setEditMode = useSetRecoilState(EditModeState);
	const setEditingClient = useSetRecoilState(EditingClientState);
	const setActiveEmailClient = useSetRecoilState(ActiveEmailClientState);
	const setActiveEmailClientMethod = useSetRecoilState(ActiveEmailClientMethodState);

	const initializeEditMode = useCallback((client: IClientEmail) => {
		setEditMode(true);
		setEditingClient(client);
		
		// Find the corresponding client configuration
		const supportedClient = SUPPORTED_CLIENT.find((sc: { methods: { key: string }[] }) => sc?.methods[0]?.key === client.provider);
		
		if (supportedClient) {
			setActiveEmailClient(supportedClient);
			
			// Set the method based on provider
			const method = supportedClient.methods[0]; // Usually the first method for the provider
			if (method) {
				setActiveEmailClientMethod(method.key);
			}
		} else {
			// eslint-disable-next-line no-console
			console.warn('Unsupported email client provider:', client.provider);
		}
	}, [setEditMode, setEditingClient, setActiveEmailClient, setActiveEmailClientMethod]);

	const resetEditMode = useCallback(() => {
		setEditMode(false);
		setEditingClient(null);
	}, [setEditMode, setEditingClient]);

	return { initializeEditMode, resetEditMode };
};

export const useEmailClient = () => {
	const [emailClientsList, setEmailClients] = useRecoilState(EmailClientsState);
	const setIsLoading = useSetRecoilState(IsEmailClientsListLoadingState);
	// global states
	const settingsKyc = useRecoilValue(kycSettingsState);
	const user = useRecoilValue(loginState);
	const activeEmailClientMethod = useRecoilValue(ActiveEmailClientMethodState);
	const credentialData = useRecoilValue(
		ActiveEmailClientFormDataState(activeEmailClientMethod)
	);
	const editMode = useRecoilValue(EditModeState);
	const editingClient = useRecoilValue(EditingClientState);
	const setEmailClientActionLoading = useSetRecoilState(EmailClientActionLoadingState);

	// hooks
	const { post, get, patch, remove } = useNetwork();
	const{errorNotification} = useNotification()

	const { whitelabel, whitelistConfig } = settingsKyc ?? {};

	const finalCredentials = useMemo(() => {
		const newCredential = { ...credentialData };
		delete newCredential.isDefault;
		Object.keys(newCredential).forEach(key => {
			if(!avoidTrimKeys[key as keyof typeof avoidTrimKeys])
			newCredential[key] = newCredential[key]?.trim() as string;
		});
		return { ...newCredential };
	}, [credentialData]);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { _id, ...rest } = whitelistConfig ?? {};

	const getEmailClients = useCallback(async () => {
		setIsLoading(true);
		const resp = await get(API_URL.EMAIL_CLIENTS+`?businessId=${user.business}`);
		setEmailClients(resp.data);
		setIsLoading(false);
		return resp;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [get, user.business, setEmailClients, setIsLoading]);

	const getSingleEmailClient = useCallback(async (clientId: string) => {
		const resp = await get(`${API_URL.EMAIL_CLIENTS}/${clientId}`);
		return resp;
	}, [get]);

	const isShowDefaultCheckbox = useMemo(() => {
		const activeClient = (emailClientsList ?? []).find(client => client.isActive);
	  
		if (editMode && editingClient) {
		  return activeClient?._id?.toString() !== editingClient._id?.toString();
		}
	  
		return !!activeClient;
	  }, [emailClientsList, editMode, editingClient]);

	const updateEmailClient = useCallback(async () => {
		const payload = {
			businessId: user.business,
			provider: activeEmailClientMethod,
			config: finalCredentials,
			isActive: credentialData.isDefault === 'true' ? true : false,
		};

		if(!isShowDefaultCheckbox) {
			payload.isActive = true;
		}
		
		let resp;
		if (editMode && editingClient) {
			// Update existing client
			delete payload.businessId;
			resp = await patch(`${API_URL.EMAIL_CLIENTS}/${editingClient._id}`, payload);
		} else {
			// Create new client
			resp = await post(API_URL.EMAIL_CLIENTS, payload);
		}
		if(resp?._id) {
			getEmailClients();
		}else {
			if(resp?.message) {
				errorNotification(resp.message)
			}
		}
		return resp;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [whitelabel, activeEmailClientMethod, whitelistConfig, rest, finalCredentials, editMode, editingClient]);

	const removeEmailClient = useCallback(
		async (clientId: string) => {
			const resp = await remove(`${API_URL.EMAIL_CLIENTS}/${clientId}`);
			if(resp) {
				getEmailClients();
			}
			return resp;
		},
		[remove, getEmailClients]
	);

	const makeAsDefaultEmailClient = useCallback(
		async (clientId: string) => {
			setEmailClientActionLoading(prev => ({...prev, [clientId]: true}));
			const resp = await patch(`${API_URL.EMAIL_CLIENTS}/${clientId}`, {isActive: true});
			setEmailClientActionLoading(prev => ({...prev, [clientId]: false}));
			if(resp) {
				getEmailClients();
			}
			return resp;
		},
		[patch, getEmailClients, setEmailClientActionLoading]
	);

	const changeEmailClient = useCallback(async (key: string) => {
		const payload = {
			whitelabel: whitelabel ?? false,
			whitelabelKey: key,
		};
		const resp = await post(API_URL.EMAIL_CLIENTS, payload);
		return resp;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [whitelabel]);

	return { 
		getEmailClients, 
		getSingleEmailClient,
		updateEmailClient, 
		removeEmailClient, 
		makeAsDefaultEmailClient,
		changeEmailClient, 
		editMode,
		editingClient
	};
};
